import i18n from 'i18n';
import cloneDeep from 'lodash.clonedeep';
import omitBy from 'lodash.omitby';
import isNil from 'lodash.isnil';
import isEqual from 'lodash.isequal';
import * as _ from 'lodash';
import { Operation } from 'data/types';
import {
  setVpnConfigsOperation,
  adaptVPNToSubmit,
  adaptVPNToCopy,
} from 'data/vpnConfig/vpnConfig.adapter';
import { removeDiacritic } from 'i18n/i18n.utils';
import { selectAfWithDsid, is5GPOI } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import { Diameter } from 'data/diameter/diameter.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { adapt5GAfsForGRToSubmit } from 'routes/AccessFunction/utils/handle5gPOIsforGR';
import { Contact } from 'data/contact/contact.types';
import { Buffer } from 'buffer';
import {
  AccessFunction,
  AccessFunctionFormData,
  AccessFunctionInterface,
  AccessFunctionInterfaceFormData,
  ACCESS_FUNCTION_INTERFACE_TEMPORARY_ID,
  AccessFunctionInterfaceFormSectionType,
  ANY_ADDRESSES,
  AdditionalMO,
  ConfiguredAccessFunction,
} from './accessFunction.types';

let nextId = 0;

const YES_NO_FIELDS = [
  'tpkt',
  'x224headerpresent',
  'imei',
  'imsi',
  'msisdn',
  'persistent',
  'keepalive',
  'tls_alpn',
  'afiribuffer',
  'dsid_supported'
];

const YES_OR_NO_FIELDS = [
  'useMono',
  'usePni',
  'usemono',
  'usepni',
];

const TRUE_FALSE_FIELDS = [
  'utcFlag',
];

const ENABLED_DISABLED_FIELDS = [
  'encryption',
];

const ENCRYPTED_FIELDS = [
  'password',
  'rmtpassword',
  'userPassword',
];

const ENCRYPTED_PLACEHOLDER = '*****';

type GroupInterfaceConfiguration = {
  [accessFunctionType: string]: {
    fieldName: keyof AccessFunctionFormData,
    fieldValue: string,
    interfaces: AccessFunctionInterfaceFormSectionType[],
  },
}

/**
 * The configuration below will be used to group the interfaces
 * for the Access Function tag used as the key.
 * The grouping is only done in the listed interface when a
 * field match the specified value.
 *
 * fieldName - the field name to check
 * fieldValue - the field value to match to the field specified in the fieldName
 * interfaces - list of interface name to group
 */
const GROUP_INTERFACES_FOR_AFS: GroupInterfaceConfiguration = {
  MVNR_PGW: {
    fieldName: 'contentDeliveryVia',
    fieldValue: 'trafficManager',
    interfaces: ['contentInterfaces'],
  },
  MVNR_SBC: {
    fieldName: 'contentDeliveryVia',
    fieldValue: 'trafficManager',
    interfaces: ['contentInterfaces'],
  },
  MVNR_CSCF: {
    fieldName: 'contentDeliveryVia',
    fieldValue: 'trafficManager',
    interfaces: ['contentInterfaces'],
  },
  MVNR_RCS_AS: {
    fieldName: 'contentDeliveryVia',
    fieldValue: 'trafficManager',
    interfaces: ['contentInterfaces'],
  },
  MVNR_RCS_MSTORE: {
    fieldName: 'contentDeliveryVia',
    fieldValue: 'trafficManager',
    interfaces: ['contentInterfaces'],
  },
};

type KeyValueString = {
  key: string
  value: string
}

type KeyValueNull = {
  key: string
  value: null
}

type KeyValueUnknown = {
  key: string
  value: unknown
}

const ALL_SENSOR_PARAMS = ['iri_last_packet_time', 'numtarget', 'numradsess', 'radeventrate', 'radlastheartbeatime', 'cc_last_packet_time'] as const;
type SensorParam = typeof ALL_SENSOR_PARAMS[number];
const SENSOR_SPECIFIC_PARAMS_TO_REMOVE: {
  [key: string]: { [key: string]: SensorParam[] };
} = {
  provisioningInterfaces: {
    IP: [],
    ETSI: [],
    RADIUS: ['numtarget']
  },
  dataInterfaces: {
    IP: ['numradsess', 'radeventrate', 'radlastheartbeatime'],
    ETSI: ['numradsess', 'radeventrate', 'radlastheartbeatime'],
    RADIUS: []
  },
  contentInterfaces: {
    IP: [],
    ETSI: [],
    RADIUS: ['cc_last_packet_time']
  }
};

export const SERVER_AF_TYPES = ['SS8_MDF2_SVR', 'SS8_MDF3_SVR', 'SS8_CCPAG_SVR', 'SS8_XCF3_SVR'];

export function adaptToForm(accessFunction: AccessFunction, accessFunctions?: AccessFunction[]): AccessFunctionFormData {
  const clonedAccessFunction = cloneDeep(accessFunction);

  const accessFunctionForm: AccessFunctionFormData = {
    ...clonedAccessFunction,
    contact: adaptContactToForm(clonedAccessFunction.contact),
    provisioningInterfaces: adaptInterfaceToForm(clonedAccessFunction.provisioningInterfaces),
    dataInterfaces: adaptInterfaceToForm(clonedAccessFunction.dataInterfaces),
    contentInterfaces: adaptInterfaceToForm(clonedAccessFunction.contentInterfaces),
    afOptions: adaptInterfaceToForm(clonedAccessFunction.afOptions),
  };

  groupInterfacesToForm(accessFunctionForm);
  deliveryViaToForm(accessFunctionForm);
  adaptAdditionalMOFieldsToForm(accessFunctionForm, clonedAccessFunction);
  adaptAXSGPFieldsToForm(accessFunctionForm);
  adaptNokiaSbcFieldsToForm(accessFunctionForm);
  adaptEricssonLIIMSCorbaFieldsToForm(accessFunctionForm);
  adaptEricssonNEFieldsToForm(accessFunctionForm, accessFunctions);
  adaptNokiaSMFAdminFieldsToForm(accessFunctionForm);
  adaptEricssonLIMSFieldsToForm(accessFunctionForm);
  adaptUtimacoFieldsToForm(accessFunctionForm);

  return omitBy<AccessFunctionFormData>(accessFunctionForm, isNil) as AccessFunctionFormData;

}

const adaptAXSGPFieldsToForm = (accessFunctionForm: AccessFunctionFormData): void => {
  if (accessFunctionForm.tag !== 'AXSGP') return;

  const { provisioningInterfaces } = accessFunctionForm;
  if (provisioningInterfaces && provisioningInterfaces[0]) {
    accessFunctionForm.serviceName = provisioningInterfaces[0].network_name;
  }
}

const adaptNokiaSbcFieldsToForm = (accessFunctionForm: AccessFunctionFormData): void => {
  if (accessFunctionForm.tag !== 'NOKIA_SBC') return;

  const { dataInterfaces } = accessFunctionForm;
  if (dataInterfaces && dataInterfaces[0]) {
    accessFunctionForm.mode = dataInterfaces[0].mode;
  }
}

const adaptEricssonLIIMSCorbaFieldsToForm = (accessFunctionForm: AccessFunctionFormData): void => {
  if (accessFunctionForm.tag === 'ERICSSON_LIMS' && accessFunctionForm.model === '') {
    accessFunctionForm.tag = 'ERICSSON_LIMS_CORBA'

    const { provisioningInterfaces } = accessFunctionForm;
    if (provisioningInterfaces && provisioningInterfaces[0]) {
      provisioningInterfaces[0].ipAddr = provisioningInterfaces[0].destIPAddr;
      provisioningInterfaces[0].port = provisioningInterfaces[0].destPort;
    }

    const { dataInterfaces } = accessFunctionForm;
    if (dataInterfaces && dataInterfaces.length > 0) {
      dataInterfaces.forEach((dataInterface) => {
        // Xcipio is support single data interface for now so we get the deliveryFormat directly from accessFunction.
        // dataInterface.deliveryFormat = accessFunctionForm.deliveryFormat;
        dataInterface.ipAddr = dataInterface.destIPAddr;
      })
    }

    const { contentInterfaces } = accessFunctionForm;
    if (contentInterfaces && contentInterfaces.length > 0) {
      contentInterfaces.forEach((contentInterface) => {
        contentInterface.ipAddr = contentInterface.destIPAddr;
        contentInterface.port = contentInterface.destPort;
      })
    }

  }
}

const adaptEricssonNEFieldsToForm = (accessFunctionForm: AccessFunctionFormData, accessFunctions?: AccessFunction[]): void => {
  if (accessFunctionForm.tag === 'ERICSSON_NE' && accessFunctionForm?.eimsNetworkElement && accessFunctionForm?.eimsNetworkElement?.length) {
    const parentid = accessFunctionForm.eimsNetworkElement[0]?.parentid;
    if (parentid) {
      const parentAF = accessFunctions?.find((accessFunction) => {
        const afid = accessFunction.provisioningInterfaces?.[0]?.options?.find(({ key }) => key === 'afid')?.value.toString() ?? ''
        return afid === parentid;
      });

      if (parentAF) {
        accessFunctionForm.version = parentAF.version;
        accessFunctionForm.model = parentAF.model;
      }
    }
  }
}

const adaptNokiaSMFAdminFieldsToForm = (accessFunctionForm: AccessFunctionFormData): void => {
  if (accessFunctionForm.tag === 'NOKIA_CMG' && accessFunctionForm.model === 'SMF_C') {
    accessFunctionForm.tag = 'NOKIA_SMF_ADMIN'
  }
}

const adaptEricssonLIMSFieldsToForm = (accessFunctionForm: AccessFunctionFormData): void => {
  if (!accessFunctionForm || accessFunctionForm.tag !== 'ERICSSON_LIMS' || !accessFunctionForm.provisioningInterfaces) {
    return;
  }
  accessFunctionForm.provisioningInterfaces[0].appprotocolver = accessFunctionForm.provisioningInterfaces[0].version;
}

const adaptUtimacoFieldsToForm = (accessFunctionForm: AccessFunctionFormData): void => {
  if (accessFunctionForm.tag !== 'SIEMENS_LIMS') {
    return;
  }
  accessFunctionForm.dataInterfaces?.forEach((dataInterface) => {
    if (!dataInterface.mctype || dataInterface.mctype === '') {
      dataInterface.mctype = dataInterface.ftpType;
    }

    const { deliveryFormat, irimcidps, irimcidcs } = dataInterface;
    if (deliveryFormat === 'TS33108' && irimcidps) {
      dataInterface.irimcid = dataInterface.irimcidps;
    }
    if (deliveryFormat === 'TS201671' && irimcidcs) {
      dataInterface.irimcid = dataInterface.irimcidcs;
    }
  });
}

export function adaptToCopy(accessFunctionForm: AccessFunctionFormData): AccessFunctionFormData {
  const clonedAccessFunction = cloneDeep(accessFunctionForm);
  clonedAccessFunction.name = removeDiacritic(i18n.t('accessFunction.copy.newName', { name: clonedAccessFunction.name }));
  clonedAccessFunction.id = '';

  const accessFunctionToCopy: AccessFunctionFormData = {
    ...clonedAccessFunction,
    provisioningInterfaces: adaptInterfaceToCopy(clonedAccessFunction.provisioningInterfaces),
    dataInterfaces: adaptInterfaceToCopy(clonedAccessFunction.dataInterfaces),
    contentInterfaces: adaptInterfaceToCopy(clonedAccessFunction.contentInterfaces),
    vpnConfig: adaptVPNToCopy(clonedAccessFunction.vpnConfig),
  };

  return omitBy<AccessFunctionFormData>(accessFunctionToCopy, isNil) as AccessFunctionFormData;
}

export function adaptDeliveryViaToSubmit(
  formData: AccessFunctionFormData,
  data: Partial<AccessFunctionFormData>,
  enabledAccessFunctions: SupportedFeaturesAccessFunctions
): AccessFunctionFormData {
  const clonedFormData = cloneDeep(formData);

  const mdf2NodeNameInProvisioningInterfaces = data?.provisioningInterfaces?.[0]?.mdf2NodeName;
  const mdf3NodeNameInProvisioningInterfaces = data?.provisioningInterfaces?.[0]?.mdf3NodeName;
  const ccnodenameInProvisioningInterfaces = data?.provisioningInterfaces?.[0]?.ccnodename;
  clonedFormData.mdf2NodeName = mdf2NodeNameInProvisioningInterfaces;
  clonedFormData.mdf3NodeName = ccnodenameInProvisioningInterfaces ?? mdf3NodeNameInProvisioningInterfaces;

  if (data?.tag === 'ERK_5GNEF') {
    clonedFormData.mdf3NodeName = mdf3NodeNameInProvisioningInterfaces ?? '';
  }

  const dataDeliveryVia = data.dataDeliveryVia ?? '';
  const contentDeliveryVia = data.contentDeliveryVia ?? '';

  if (dataDeliveryVia === 'ss8Distributor') {
    clonedFormData.mdf2NodeName = data.mdf2NodeName;
  }

  if (contentDeliveryVia === 'ss8Distributor') {
    clonedFormData.mdf3NodeName = data.mdf3NodeName;
  }

  const nodeNameType = clonedFormData?.provisioningInterfaces?.[0]?.nodeNameType;
  if (nodeNameType) {
    if (nodeNameType === 'mdf3NodeName' && clonedFormData.provisioningInterfaces && clonedFormData.provisioningInterfaces.length) {
      clonedFormData.provisioningInterfaces[0].ccnodename = mdf3NodeNameInProvisioningInterfaces;
      clonedFormData.mdf3NodeName = mdf3NodeNameInProvisioningInterfaces;
    } else if (nodeNameType === 'ccnodename') {
      clonedFormData.mdf3NodeName = ccnodenameInProvisioningInterfaces;
    }

    if (is5GPOI(clonedFormData.tag, enabledAccessFunctions) && clonedFormData.provisioningInterfaces && clonedFormData.provisioningInterfaces.length) {
      delete clonedFormData.provisioningInterfaces[0].ccnodename;
    }
  }

  return clonedFormData;
}

export function adaptToSubmit(
  accessFunctionForm: AccessFunctionFormData,
  originalAccessFunction?: AccessFunction | null,
  operation?: Operation,
  fileLocationFormData?: { key: string; value: string; },
): AccessFunction {
  const clonedAccessFunctionForm = cloneDeep(accessFunctionForm);
  ungroupInterfacesToSubmit(clonedAccessFunctionForm);
  removeDeliveryViaFields(clonedAccessFunctionForm);
  removeUnexpectedFields(clonedAccessFunctionForm);
  adaptKodiakPTTFields(clonedAccessFunctionForm);
  adaptAxsgpInterfaces(clonedAccessFunctionForm);
  adaptNokiaSbcFields(clonedAccessFunctionForm);
  adapt5GAfsForGRToSubmit(clonedAccessFunctionForm);
  adaptDiameterFieldsPayload(clonedAccessFunctionForm);

  if (
    clonedAccessFunctionForm.type === 'ERICSSON_LIMS'
    || clonedAccessFunctionForm.type === 'ERICSSON_LIMS_CORBA'
    || clonedAccessFunctionForm.tag === 'COGNYTE'
    || clonedAccessFunctionForm.tag === 'VRNT_SG'
    || clonedAccessFunctionForm.tag === 'SIEMENS_LIMS'
  ) {
    let deliveryFormat = clonedAccessFunctionForm?.dataInterfaces?.length ? clonedAccessFunctionForm?.dataInterfaces[0]?.deliveryFormat : '';
    if (clonedAccessFunctionForm.tag === 'SIEMENS_LIMS') {
      const mctype = clonedAccessFunctionForm?.dataInterfaces?.length ? clonedAccessFunctionForm?.dataInterfaces[0]?.mctype : '';
      if (mctype === 'TCP') {
        deliveryFormat = undefined;
      }
    }
    clonedAccessFunctionForm.deliveryFormat = deliveryFormat;
  }

  if (clonedAccessFunctionForm.afOptions && clonedAccessFunctionForm.afOptions.length) {
    const { provisionTargetTime } = clonedAccessFunctionForm.afOptions[0];
    clonedAccessFunctionForm.preprovisioningLead = provisionTargetTime === '' ? '000:00' : provisionTargetTime;
    delete clonedAccessFunctionForm.afOptions[0].provisionTargetTime;

    clonedAccessFunctionForm.afOptions[0].blkotrid = clonedAccessFunctionForm.afOptions[0].blkotrid || clonedAccessFunctionForm.afOptions[0].blkotrid === 'Y' ? 'Y' : 'N';
    clonedAccessFunctionForm.afOptions[0].blkttrid = clonedAccessFunctionForm.afOptions[0].blkttrid || clonedAccessFunctionForm.afOptions[0].blkttrid === 'Y' ? 'Y' : 'N';
  }

  if (clonedAccessFunctionForm.provisioningInterfaces && clonedAccessFunctionForm.provisioningInterfaces.length) {
    if (clonedAccessFunctionForm.tag === 'STA_GGSN') {
      if (clonedAccessFunctionForm.provisioningInterfaces[0].context === '') {
        clonedAccessFunctionForm.provisioningInterfaces[0].context = 'notconfigured';
      }
    }
  }

  const accessFunction: AccessFunction = {
    ...clonedAccessFunctionForm,
    contact: adaptContactToSubmit(clonedAccessFunctionForm.contact),
    provisioningInterfaces: adaptInterfaceToSubmit(clonedAccessFunctionForm.provisioningInterfaces),
    dataInterfaces: adaptInterfaceToSubmit(clonedAccessFunctionForm.dataInterfaces, accessFunctionForm, fileLocationFormData),
    contentInterfaces: adaptInterfaceToSubmit(clonedAccessFunctionForm.contentInterfaces),
    afOptions: adaptInterfaceToSubmit(clonedAccessFunctionForm.afOptions),
    vpnConfig: adaptVPNToSubmit(clonedAccessFunctionForm.vpnConfig),
    monitoringCenters: adaptInterfaceToSubmit(clonedAccessFunctionForm.monitoringCenters),
    territories: adaptInterfaceToSubmit(clonedAccessFunctionForm.territories),
    eimsNetworkElement: adaptInterfaceToSubmit(clonedAccessFunctionForm.eimsNetworkElement),
    diameter: adaptInterfaceToSubmit(clonedAccessFunctionForm.diameter),
    manualEntry: adaptInterfaceToSubmit(clonedAccessFunctionForm.manualEntry),
    ipAssignment: adaptInterfaceToSubmit(clonedAccessFunctionForm.ipAssignment),
    fileImportExport: adaptInterfaceToSubmit(clonedAccessFunctionForm.fileImportExport),
    usnbdAgencies: adaptUsnbdAgenciesToSubmit(clonedAccessFunctionForm.usnbdAgencies),
    usnbdCallDataChannels: adaptInterfaceToSubmit(clonedAccessFunctionForm.usnbdCallDataChannels),
  };

  if (clonedAccessFunctionForm.type === 'SIEMENS_LIMS') {
    accessFunction.dataInterfaces?.forEach((dataInterface) => {
      const mcType = dataInterface.options.find((item) => item.key === 'mctype')
      if (mcType) {
        dataInterface.transportType = mcType.value;
        dataInterface.options.push({ key: 'fptType', value: mcType.value });
      }
    });
  }

  adaptAdditionalMOFields(accessFunction, originalAccessFunction, operation);

  // Set entries to be removed before returning
  if (!clonedAccessFunctionForm.afOptions || !clonedAccessFunctionForm.afOptions.length) {
    accessFunction.afOptions = undefined;
  }
  accessFunction.monitoringCenters = undefined;
  accessFunction.territories = undefined;
  accessFunction.eimsNetworkElement = undefined;
  accessFunction.diameter = undefined;
  accessFunction.manualEntry = undefined;
  accessFunction.ipAssignment = undefined;
  accessFunction.fileImportExport = undefined;
  accessFunction.usnbdAgencies = undefined;
  accessFunction.usnbdCallDataChannels = undefined;

  // Remove any undefined entry
  return omitBy<AccessFunction>(accessFunction, isNil) as AccessFunction;
}

function adaptKodiakPTTFields(accessFunctionForm: AccessFunctionFormData): void {
  if (accessFunctionForm.tag !== 'KODIAK_PTT') {
    return;
  }

  const { dataInterfaces } = accessFunctionForm;
  if (dataInterfaces == null || dataInterfaces.length < 2) {
    return;
  }

  // For Kodiak PTT, there is a hidden field called persistent which the first
  // dataInterface has value set as true and the second value set as false.
  // Because the template can only have one initial value (true), the false
  // value is manually set here for the second interface if it exists.
  dataInterfaces[1].persistent = false;
}

function adaptAxsgpInterfacesParams(
  accessFunctionInterfaceFormData: AccessFunctionInterfaceFormData[],
  accessFunctionForm: AccessFunctionFormData,
  interfaceKey: keyof AccessFunctionFormData,
  sensorType: null | 'IP' | 'RADIUS' | 'ETSI'
): void {
  if (accessFunctionForm.serviceName && interfaceKey === 'provisioningInterfaces') {
    accessFunctionInterfaceFormData.forEach((entry) => {
      entry.network_name = accessFunctionForm.serviceName;
    });
    delete accessFunctionForm.serviceName;
  }

  if (!sensorType) {
    // remove all sensor params (ex: model 7002, or model 6002)
    accessFunctionInterfaceFormData.forEach((entry) => {
      ALL_SENSOR_PARAMS.forEach((k) => {
        if (entry[k]) {
          entry[k] = undefined;
        }
      });
    });
    return;
  }

  accessFunctionInterfaceFormData.forEach((entry) => {
    SENSOR_SPECIFIC_PARAMS_TO_REMOVE[interfaceKey][sensorType].forEach((k) => {
      if (entry[k]) {
        entry[k] = undefined;
      }
    });
  });

}

function adaptAxsgpInterfaces(accessFunctionForm: AccessFunctionFormData): void {
  if (accessFunctionForm.tag !== 'AXSGP') {
    return;
  }

  let type = null as ('IP' | 'ETSI' | 'RADIUS' | null);
  if (accessFunctionForm.contentInterfaces && accessFunctionForm.contentInterfaces.length && accessFunctionForm.contentInterfaces[0].type) {
    type = accessFunctionForm.contentInterfaces[0].type as ('IP' | 'ETSI' | 'RADIUS' | null);
  }

  if (accessFunctionForm.contentDeliveryVia === 'trafficManager') {
    return;
  }

  adaptAxsgpInterfacesParams(
    accessFunctionForm.provisioningInterfaces || [],
    accessFunctionForm,
    'provisioningInterfaces',
    type);

  adaptAxsgpInterfacesParams(
    accessFunctionForm.dataInterfaces || [],
    accessFunctionForm,
    'dataInterfaces',
    type);

  adaptAxsgpInterfacesParams(
    accessFunctionForm.contentInterfaces || [],
    accessFunctionForm,
    'contentInterfaces',
    type);

  if (type === 'IP' || type === 'ETSI') {
    if (!isValidInterfaceAxsgp(accessFunctionForm.provisioningInterfaces)) {
      accessFunctionForm.provisioningInterfaces = [];
    }
    if (!isValidInterfaceAxsgp(accessFunctionForm.dataInterfaces)) {
      accessFunctionForm.dataInterfaces = [];
    }
    if (!isValidInterfaceAxsgp(accessFunctionForm.contentInterfaces)) {
      accessFunctionForm.contentInterfaces = [];
    }
  } else if (type === 'RADIUS') {
    if (!isValidInterfaceAxsgp(accessFunctionForm.provisioningInterfaces)) {
      accessFunctionForm.provisioningInterfaces = [];
    }
    if (!isValidInterfaceAxsgp(accessFunctionForm.contentInterfaces)) {
      accessFunctionForm.contentInterfaces = [];
    }
  }
}

function adaptDiameterFieldsPayload(accessFunctionForm: AccessFunctionFormData): void {
  if (accessFunctionForm.tag !== 'ERICSSON_IMS') {
    return;
  }

  const diameter = _.first(accessFunctionForm.diameter);
  if (diameter) {
    (accessFunctionForm.dataInterfaces || []).forEach((i) => {
      i.dsid = diameter.dsid;
    });
  }
}

function isValidInterfaceAxsgp(accessFunctionInterface: AccessFunctionInterfaceFormData[] | undefined) {
  if (accessFunctionInterface && accessFunctionInterface.length) {
    if (!accessFunctionInterface.every((pi) => !!pi.destIPAddr)) {
      return false;
    }
  }

  return true;
}

function adaptNokiaSbcFields(accessFunctionForm: AccessFunctionFormData): void {
  if (accessFunctionForm.tag !== 'NOKIA_SBC') {
    return;
  }
  if (accessFunctionForm.dataInterfaces && accessFunctionForm.dataInterfaces.length > 0) {
    accessFunctionForm.dataInterfaces[0].mode = accessFunctionForm.mode;
  }
  delete accessFunctionForm.mode;
}

function isAdditionalMOAccessFunction(accessFunctionForm: AccessFunction | AccessFunctionFormData): boolean {
  return accessFunctionForm.tag === 'DRVIRTUAL' ||
    accessFunctionForm.tag === 'VRNT_SG' ||
    accessFunctionForm.tag === 'COGNYTE' ||
    accessFunctionForm.tag === 'ERICSSON_NE' ||
    accessFunctionForm.tag === 'ERICSSON_IMS' ||
    accessFunctionForm.tag === 'RIBBONC20_CSC';
}

function adaptVirtualDRMediationFields(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
  originalAccessFunction?: AccessFunction | null,
): void {
  if (accessFunctionForm.tag !== 'DRVIRTUAL') {
    return;
  }

  if (accessFunctionForm.locationDrVirtual == null || accessFunctionForm.drStatus == null) {
    return;
  }

  // For Virtual DR Mediation Function, there is a hidden field called "additionalMOs" which
  // contains "moName", and "options" with "location" and "drstate" replicating
  // "locationDrVirtual" and "drStatus" fields
  const additionalMOs = originalAccessFunction?.additionalMOs ?? [];

  if (additionalMOs.length === 0) {
    additionalMOs[0] = {
      moName: 'AFINFO',
      options: [
        {
          key: 'location',
          value: accessFunctionForm.locationDrVirtual,
        },
        {
          key: 'drstate',
          value: accessFunctionForm.drStatus,
        },
      ],
    };
  } else {

    additionalMOs[0].options.forEach((option) => {

      if (option.key === 'location') {
        option.value = accessFunctionForm.locationDrVirtual || '';
      }

      if (option.key === 'drstate') {
        option.value = accessFunctionForm.drStatus || '';
      }
    });
  }

  accessFunctionForm.additionalMOs = additionalMOs;
}

function filterOptionsFields(
  accessFunctionInterface: AccessFunctionInterface,
  operation: Operation = 'CREATE',
  ifid: string,
  afid: string,
  afKey: string,
): { key: string; value: string | boolean; }[] {

  let notSendableKeys = operation === 'CREATE'
    ? ['moName', 'afid', 'ifid', 'cdtype', 'cdport']
    : ['moName', 'cdtype', 'cdport'];

  if (afKey === 'COGNYTE') {
    notSendableKeys = notSendableKeys.filter((key) => key !== 'cdtype' && key !== 'cdport');
  }

  return accessFunctionInterface.options
    .map((option) => {

      if (option.key === 'ifid' && option.value === '' && operation === 'UPDATE') {
        return {
          ...option,
          value: ifid,
        };
      }

      if (option.key === 'afid' && option.value === '' && operation === 'UPDATE') {
        return {
          ...option,
          value: afid,
        };
      }

      return option;
    })
    .filter((option) => !notSendableKeys.includes(option.key));
}

function filterOptionsFieldsForUsnbd(
  accessFunctionInterface: AccessFunctionInterface,
  operation: Operation = 'CREATE',
  ifid: string,
  afid: string,
): { key: string; value: string | boolean; }[] {

  const notSendableKeys = operation === 'CREATE' || operation === 'UPDATE'
    ? ['moName', 'afid', 'cdtype', 'cdport', 'cdcindex', 'cdcstatus', 'status']
    : ['moName', 'cdtype', 'cdport'];

  return accessFunctionInterface.options
    .map((option) => {

      if (option.key === 'ifid' && option.value === '' && operation === 'UPDATE') {
        return {
          ...option,
          value: ifid,
        };
      }

      if (option.key === 'afid' && option.value === '' && operation === 'UPDATE') {
        return {
          ...option,
          value: afid,
        };
      }

      return option;
    })
    .filter((option) => !notSendableKeys.includes(option.key));
}

function getAdditionalMo(accessFunction: AccessFunction, moName: string): AdditionalMO[] {

  const additionalMo = accessFunction
    ?.provisioningInterfaces?.[0]?.additionalMOs?.filter((mo) => mo.moName === moName) ?? [];

  return additionalMo;
}

function getLastAdditionalMoId(moIdKey: string, moName: string, accessFunction?: AccessFunction | null): number {

  if (accessFunction == null) {
    return 0;
  }

  const additionalMos = getAdditionalMo(accessFunction, moName);

  const [last] = additionalMos.map((mo) => {
    const id = mo.options?.find((option) => option.key === moIdKey)?.value;
    return id;
  })?.sort()?.reverse();

  return +(last ?? 0);
}

function setAdditionalMoOptionId(
  accessFunctionForm: AccessFunction,
  additionalMoKey: 'monitoringCenters' | 'territories' | 'usnbdAgencies' | 'usnbdCallDataChannels',
  moName: string,
  moIdKey: string,
  originalAccessFunction?: AccessFunction | null,
): AccessFunctionInterface[] {

  const greaterMcId = getLastAdditionalMoId(moIdKey, moName, originalAccessFunction);

  const additionalMos = accessFunctionForm[additionalMoKey];

  if (additionalMos == null) {
    return [];
  }

  return additionalMos.map((mc, index) => ({
    ...mc,
    options: mc.options.map((option) => {
      if (option.key === moIdKey && option.value === '') {
        return { ...option, value: (index + 1 + greaterMcId).toString() };
      }
      return option;
    }),
  }));
}

function adaptVerintStarGateFields(
  accessFunctionForm: AccessFunction,
  originalAccessFunction?: AccessFunction | null,
  operation?: Operation,
): void {
  if (accessFunctionForm.tag !== 'VRNT_SG' && accessFunctionForm.tag !== 'COGNYTE') {
    return;
  }

  if (
    accessFunctionForm.provisioningInterfaces == null ||
    accessFunctionForm.monitoringCenters == null ||
    accessFunctionForm.territories == null
  ) {
    return;
  }

  const newMonitoringCenters = setAdditionalMoOptionId(accessFunctionForm, 'monitoringCenters', 'VRNTSGMC', 'mcid', originalAccessFunction);
  const newTerritories = setAdditionalMoOptionId(accessFunctionForm, 'territories', 'VRNTSGTERR', 'terrid', originalAccessFunction);

  const ifId = newMonitoringCenters[0].options.find((option) => option.key === 'ifid')?.value || '';
  const afid = newMonitoringCenters[0].options.find((option) => option.key === 'afid')?.value || '';

  accessFunctionForm.provisioningInterfaces[0].additionalMOs = [
    ...newMonitoringCenters,
    ...newTerritories,
  ]
    .map((additionalMO) => ({
      id: additionalMO.id || '',
      moName: additionalMO.moName || (additionalMO.options.find((option) => option.key === 'moName')?.value || '').toString(),
      options: filterOptionsFields(additionalMO, operation, ifId.toString(), afid.toString(), accessFunctionForm.tag),
    }));
}

function adaptUsnbdAgencyFields(
  accessFunctionForm: AccessFunction,
  originalAccessFunction?: AccessFunction | null,
  operation?: Operation,
): void {
  if (accessFunctionForm.tag !== 'RIBBONC20_CSC') {
    return;
  }

  if (
    accessFunctionForm.provisioningInterfaces == null ||
    accessFunctionForm.usnbdAgencies == null
  ) {
    return;
  }

  const newUsnbdAgencies = setAdditionalMoOptionId(accessFunctionForm, 'usnbdAgencies', 'CS2KAGENCY', 'agencyid', originalAccessFunction);

  const ifId = newUsnbdAgencies[0].options.find((option) => option.key === 'ifid')?.value || '';
  const afid = newUsnbdAgencies[0].options.find((option) => option.key === 'afid')?.value || '';

  accessFunctionForm.provisioningInterfaces[0].additionalMOs = [
    ...newUsnbdAgencies,
  ]
    .map((additionalMO) => ({
      id: additionalMO.id || '',
      moName: additionalMO.moName || (additionalMO.options.find((option) => option.key === 'moName')?.value || '').toString(),
      options: filterOptionsFieldsForUsnbd(additionalMO, operation, ifId.toString(), afid.toString()),
    }));
}

function adaptUsnbdCallDataChannelFields(
  accessFunctionForm: AccessFunction,
  originalAccessFunction?: AccessFunction | null,
  operation?: Operation,
): void {
  if (accessFunctionForm.tag !== 'RIBBONC20_CSC') {
    return;
  }

  if (
    accessFunctionForm.dataInterfaces == null ||
    accessFunctionForm.usnbdCallDataChannels == null
  ) {
    return;
  }

  const newUsnbdCallDataChannels = setAdditionalMoOptionId(accessFunctionForm, 'usnbdCallDataChannels', 'CS2KCDC', 'cdcId', originalAccessFunction);

  const ifId = newUsnbdCallDataChannels[0].options.find((option) => option.key === 'ifid')?.value || '';
  const afid = newUsnbdCallDataChannels[0].options.find((option) => option.key === 'afid')?.value || '';

  accessFunctionForm.dataInterfaces[0].additionalMOs = [
    ...newUsnbdCallDataChannels,
  ]
    .map((additionalMO) => ({
      id: additionalMO.id || '',
      moName: additionalMO.moName || (additionalMO.options.find((option) => option.key === 'moName')?.value || '').toString(),
      options: filterOptionsFieldsForUsnbd(additionalMO, operation, ifId.toString(), afid.toString()),
    }));
}

function adaptEricssonNeFields(
  accessFunctionForm: AccessFunction,
  originalAccessFunction?: AccessFunction | null,
): void {
  if (accessFunctionForm.tag !== 'ERICSSON_NE') {
    return;
  }

  const additionalMOs = accessFunctionForm.eimsNetworkElement?.map((ne) => ({
    moName: 'EIMSNE',
    options: [{ key: 'name', value: ne.name }, ...(ne?.options || [])],
  })) as AdditionalMO[];

  accessFunctionForm.additionalMOs = additionalMOs;
}

function adaptAdditionalMOFields(
  accessFunctionForm: AccessFunction,
  originalAccessFunction?: AccessFunction | null,
  operation?: Operation,
): void {
  if (!isAdditionalMOAccessFunction(accessFunctionForm)) {
    return;
  }

  adaptVirtualDRMediationFields(accessFunctionForm, originalAccessFunction);
  adaptVerintStarGateFields(accessFunctionForm, originalAccessFunction, operation);
  adaptEricssonNeFields(accessFunctionForm, originalAccessFunction);
  adaptDiameterMoFields(accessFunctionForm)
  adaptUsnbdAgencyFields(accessFunctionForm, originalAccessFunction, operation);
  adaptUsnbdCallDataChannelFields(accessFunctionForm, originalAccessFunction, operation);
}

export function setAdditionalMOOperation(
  accessFunction: AccessFunction,
  operation: Operation = 'CREATE',
  accessFunctions?: AccessFunction[],
  diameter?: Diameter
): void {
  if (!isAdditionalMOAccessFunction(accessFunction) || accessFunction?.additionalMOs?.length === 0) {
    return;
  }

  const operationMO = getAdditionMoOperation(operation, accessFunction, accessFunctions, diameter);

  accessFunction?.additionalMOs?.forEach((additionalMO) => {
    additionalMO.operation = operationMO;
  });
}

const getAdditionMoOperation = (
  operation: Operation = 'CREATE',
  accessFunction: AccessFunction,
  accessFunctions?: AccessFunction[],
  diameter?: Diameter
) => {

  if (operation !== 'CREATE') {
    return operation;
  }

  if (accessFunction.tag === 'ERICSSON_IMS' && diameter != null) {
    return 'UPDATE';
  }

  if (
    accessFunction.tag !== 'ERICSSON_IMS' ||
    !accessFunctions?.length ||
    accessFunctions.find(selectAfWithDsid) == null
  ) {
    return operation;
  }

  return 'UPDATE';
}

const adaptVRNTSGadditionalToForm = (
  accessFunctionInterface: AccessFunctionInterface,
  interfaceMoName: string,
): AccessFunctionInterfaceFormData[] => {

  const { additionalMOs } = accessFunctionInterface;

  if (additionalMOs == null) {
    return [];
  }

  return additionalMOs
    .filter((additionalMO) => additionalMO.moName === interfaceMoName)
    .map(({ id, moName, options }) => ({
      id,
      moName,
      ...options?.reduce(
        (previousValue, currentValue) => ({
          ...previousValue,
          [currentValue.key]: currentValue.value,
        }),
        {}
      ),
    } as AccessFunctionInterfaceFormData));
};

const adaptAdditionalMOOptionsToForm = (
  additionalMOs: AdditionalMO[],
  interfaceMoName: string,
): AccessFunctionInterfaceFormData[] => {

  if (additionalMOs == null) {
    return [];
  }

  return additionalMOs
    .filter((additionalMO) => additionalMO.moName === interfaceMoName)
    .map(({ id, moName, options }) => ({
      id,
      moName,
      ...options?.reduce(
        (previousValue, currentValue) => ({
          ...previousValue,
          [currentValue.key]: currentValue.value,
        }),
        {}
      ),
    } as AccessFunctionInterfaceFormData));
};

function adaptVRNTSGAdditionalMOFieldsToForm(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
  originalAccessFunction?: AccessFunction,
): void {

  if ((accessFunctionForm.tag !== 'VRNT_SG' && accessFunctionForm.tag !== 'COGNYTE') || originalAccessFunction == null) {
    return;
  }

  const { provisioningInterfaces } = originalAccessFunction;

  if (provisioningInterfaces == null || provisioningInterfaces?.[0]?.additionalMOs == null) {
    return;
  }

  accessFunctionForm.monitoringCenters = adaptVRNTSGadditionalToForm(provisioningInterfaces[0], 'VRNTSGMC');
  accessFunctionForm.territories = adaptVRNTSGadditionalToForm(provisioningInterfaces[0], 'VRNTSGTERR');
}

function adaptClaroPRMOFieldsToForm(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
  originalAccessFunction?: AccessFunction,
): void {

  if (accessFunctionForm.tag !== 'RIBBONC20_CSC' || originalAccessFunction == null) {
    return;
  }

  const { provisioningInterfaces, dataInterfaces } = originalAccessFunction;

  if (provisioningInterfaces == null || provisioningInterfaces?.[0]?.additionalMOs == null) {
    return;
  }

  accessFunctionForm.usnbdAgencies = adaptVRNTSGadditionalToForm(provisioningInterfaces[0], 'CS2KAGENCY');

  if (dataInterfaces == null || dataInterfaces?.[0]?.additionalMOs == null) {
    return;
  }
  accessFunctionForm.usnbdCallDataChannels = adaptVRNTSGadditionalToForm(dataInterfaces[0], 'CS2KCDC');
}

function adaptEricssonNEAdditionalMOFieldsToForm(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
  originalAccessFunction?: AccessFunction,
): void {

  if (accessFunctionForm.tag !== 'ERICSSON_NE' || originalAccessFunction == null) {
    return;
  }

  const { additionalMOs } = originalAccessFunction;

  if (additionalMOs == null) {
    return;
  }

  accessFunctionForm.eimsNetworkElement = adaptAdditionalMOOptionsToForm(additionalMOs, 'EIMSNE');
}

function adaptEricssonIMSAdditionalMOFieldsToForm(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
  originalAccessFunction?: AccessFunction,
): void {

  const additionalMOs = originalAccessFunction?.dataInterfaces?.[0]?.additionalMOs;

  if (additionalMOs?.[0] == null || additionalMOs?.[0]?.moName !== 'DIAMETER') {
    return;
  }

  accessFunctionForm.diameter = adaptAdditionalMOOptionsToForm(additionalMOs, 'DIAMETER')
}

function adaptDRVirtualAdditionalMOFieldsToForm(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
): void {

  if (accessFunctionForm.tag !== 'DRVIRTUAL' || accessFunctionForm.additionalMOs == null) {
    return;
  }

  accessFunctionForm.locationDrVirtual = accessFunctionForm.additionalMOs[0].options.find((option) => option.key === 'location')?.value.toString();
  accessFunctionForm.drStatus = accessFunctionForm.additionalMOs[0].options.find((option) => option.key === 'drstate')?.value.toString();

}

function adaptAdditionalMOFieldsToForm(
  accessFunctionForm: AccessFunction | AccessFunctionFormData,
  originalAccessFunction?: AccessFunction,
): void {
  if (!isAdditionalMOAccessFunction(accessFunctionForm)) {
    return;
  }

  adaptVRNTSGAdditionalMOFieldsToForm(accessFunctionForm, originalAccessFunction);
  adaptClaroPRMOFieldsToForm(accessFunctionForm, originalAccessFunction);
  adaptDRVirtualAdditionalMOFieldsToForm(accessFunctionForm);
  adaptEricssonNEAdditionalMOFieldsToForm(accessFunctionForm, originalAccessFunction);
  adaptEricssonIMSAdditionalMOFieldsToForm(accessFunctionForm, originalAccessFunction);
}

function removeUnexpectedFields(accessFunctionForm: AccessFunctionFormData) {
  if (accessFunctionForm.tag === 'SONUS_SBC') {
    const dataInterface = accessFunctionForm.dataInterfaces?.[0];
    if (dataInterface == null) {
      return;
    }

    const { deliveryVia } = dataInterface;

    if (deliveryVia === 'VoIP') {
      delete dataInterface.sharedsecret;
      delete dataInterface.version;
    } else {
      delete dataInterface.secinfoid;
      delete dataInterface.dscp;
    }

  } else if (accessFunctionForm.type === 'PCRF') {
    deletePropertyIfEqualToFieldValue(accessFunctionForm.provisioningInterfaces, 'ownipaddr', '');
    deletePropertyIfEqualToFieldValue(accessFunctionForm.dataInterfaces, 'ownipaddr', '');
  }
}

function deletePropertyIfEqualToFieldValue(
  accessFunctionInterfaces: AccessFunctionInterfaceFormData[] | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: unknown,
): void {
  if (accessFunctionInterfaces == null) {
    return;
  }

  accessFunctionInterfaces.forEach((entry) => {
    if (entry[fieldName] === fieldValue) {
      delete entry[fieldName];
    }
  });
}

export function setAccessFunctionOperationValues(
  accessFunction: AccessFunction,
  originalAccessFunction: AccessFunction | null = null,
  operation: Operation = 'CREATE',
): AccessFunction {
  const clonedAccessFunction = cloneDeep(accessFunction);

  if (operation !== 'CREATE') {
    clonedAccessFunction.operation = operation;
  }

  setInterfacesOperation(clonedAccessFunction, originalAccessFunction);

  setVpnConfigsOperation(clonedAccessFunction.vpnConfig, originalAccessFunction?.vpnConfig);

  return clonedAccessFunction;
}

function setInterfacesOperation(
  accessFunction: AccessFunction,
  originalAccessFunction: AccessFunction | null = null,
): void {
  setInterfaceOperationValueBasedInOriginal(
    accessFunction.provisioningInterfaces,
    originalAccessFunction?.provisioningInterfaces,
  );
  setInterfaceOperationValueBasedInOriginal(
    accessFunction.contentInterfaces,
    originalAccessFunction?.contentInterfaces,
  );
  setInterfaceOperationValueBasedInOriginal(
    accessFunction.dataInterfaces,
    originalAccessFunction?.dataInterfaces,
  );
  setInterfaceOperationValueBasedInOriginal(
    accessFunction.afOptions,
    originalAccessFunction?.afOptions,
  );
}

function setInterfaceOperationValueBasedInOriginal(
  interfaces: AccessFunctionInterface[] | undefined,
  originalInterfaces: AccessFunctionInterface[] | undefined,
) {

  // Check which interfaces will be marked as CREATE, UPDATE or NO_OPERATION
  interfaces?.forEach((interfaceEntry) => {
    const originalInterfaceEntry = originalInterfaces?.find(({ id }) => id === interfaceEntry.id);
    if (originalInterfaceEntry == null) {
      interfaceEntry.operation = 'CREATE';
    } else if (!isEqual(interfaceEntry, originalInterfaceEntry)) {
      interfaceEntry.operation = 'UPDATE';
    } else {
      interfaceEntry.operation = 'NO_OPERATION';
    }

    interfaceEntry?.additionalMOs?.forEach((additionalMO) => {
      const additionalMOEntry = originalInterfaceEntry?.additionalMOs?.find(({ id }) => id === additionalMO.id);

      if (additionalMOEntry == null) {
        additionalMO.operation = 'CREATE';
      } else if (!isEqual(additionalMOEntry, additionalMO)) {
        additionalMO.operation = 'UPDATE';
      } else {
        additionalMO.operation = 'NO_OPERATION';
      }
    });

    const additionalMOsToDelete = originalInterfaceEntry?.additionalMOs
      ?.filter((originalMO) =>
        !interfaceEntry?.additionalMOs?.find(({ id, moName }) => originalMO.id === id && originalMO.moName === moName)
      )
      ?.map((originalMO) => ({
        ...originalMO,
        operation: 'DELETE',
      }));

    if (additionalMOsToDelete) {
      interfaceEntry?.additionalMOs?.push(...(additionalMOsToDelete as AdditionalMO[]));
    }

  });

  // Check which interfaces will be marked as DELETE
  const interfacesToDelete =
    originalInterfaces?.filter((originalInterfaceEntry) => {
      return !interfaces?.find(({ id }) => id === originalInterfaceEntry.id);
    })
      .map((originalInterfaceEntry) => ({
        ...originalInterfaceEntry,
        operation: 'DELETE',
      }));

  if (interfacesToDelete) {
    interfaces?.push(...(interfacesToDelete as AccessFunctionInterface[]));
  }
}

function adaptContactToForm(
  contact: Contact | undefined,
): string | undefined {
  if (!contact) {
    return '';
  }

  return contact.id;
}

function adaptInterfaceToForm(
  accessFunctionInterfaces: AccessFunctionInterface[] | undefined,
): AccessFunctionInterfaceFormData[] | undefined {
  if (!accessFunctionInterfaces) {
    return accessFunctionInterfaces;
  }

  return accessFunctionInterfaces.map(({ id, name, options }) => ({
    id,
    name,
    ...options?.map(convertYNValuesToBoolean)
      .map(convertYesNoValuesToBoolean)
      .map(convertDefaultAnyAddressToEmptyString)
      .reduce((previousValue, currentValue) => ({
        ...previousValue,
        [currentValue.key]: currentValue.value,
      }), {}),
  }));
}

function adaptInterfaceToCopy(
  accessFunctionInterfaceFormData: AccessFunctionInterfaceFormData[] | undefined,
): AccessFunctionInterfaceFormData[] | undefined {
  if (accessFunctionInterfaceFormData == null) {
    return accessFunctionInterfaceFormData;
  }

  return accessFunctionInterfaceFormData?.map(({ id, name, ...rest }) => {
    nextId += 1;

    return {
      ...rest,
      id: `${nextId}.${ACCESS_FUNCTION_INTERFACE_TEMPORARY_ID}`,
    } as AccessFunctionInterfaceFormData;

  });
}

function adaptContactToSubmit(
  contact: string | undefined,
): Contact | undefined {
  if (!contact) {
    return {
      id: '',
      name: '',
    };
  }

  return {
    id: contact,
    name: atob(contact),
  };
}

function adaptInterfaceToSubmit(
  accessFunctionInterfacesForm: AccessFunctionInterfaceFormData[] | undefined,
  accessFunctionForm?: AccessFunctionFormData,
  fileLocationFormData?: { key: string; value: string; },
): AccessFunctionInterface[] {
  if (!accessFunctionInterfacesForm) {
    return [];
  }

  return accessFunctionInterfacesForm.map((entry) => {
    let { id } = entry;

    const {
      name,
      id: _id,
      state: _state,
      deliveryVia,
      ...everythingElse
    } = entry;

    if (id?.includes(ACCESS_FUNCTION_INTERFACE_TEMPORARY_ID)) {
      id = '';
    }

    // Convert everything else to options and remove the null/undefined ones
    const options = Object.entries(everythingElse)
      .map(([key, value]) => ({ key, value }))
      .map(convertEncryptedValues)
      .map(removeUnnecessaryFields(entry, 'SIEMENS_LIMS', 'irimcid', '0', ['irimcid'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'SIEMENS_LIMS', 'deliveryFormat', 'TS201671', ['ipAddr', 'netid', 'password', 'settingIpaddr', 'irimcidps', 'leaps'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'SIEMENS_LIMS', 'deliveryFormat', 'TS33108', ['remoteIp', 'remotePort', 'isdn', 'cugilc', 'cugdnic', 'cprisdn', 'leacs', 'irimcidcs'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'SIEMENS_LIMS', 'mctype', 'TCP', ['userName', 'password', 'destPort', 'deliveryFormat', 'destIPAddr', 'leacs', 'appProtocol', 'version'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'SIEMENS_LIMS', 'mctype', 'FTP', ['ipaddr', 'port', 'lea'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'SIEMENS_LIMS', 'mctype', 'FTP', ['ipaddr', 'port', 'lea'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'ERICSSON_LIMS', 'deliveryFormat', 'TS201671', ['ipaddr', 'netid', 'password', 'naming'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'ERICSSON_LIMS', 'deliveryFormat', 'TS33108', ['remoteIp', 'remotePort', 'userPassword'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'ERICSSON_LIMS_CORBA', 'deliveryFormat', 'TS201671', ['ipAddr', 'port', 'password'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'ERICSSON_LIMS_CORBA', 'deliveryFormat', 'TS33108', ['ftpipaddr', 'ftpusername', 'ftppassword'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'VRNT_SG', 'deliveryFormat', 'TS201671', ['password'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'VRNT_SG', 'deliveryFormat', 'TS33108', ['userPassword'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'COGNYTE', 'deliveryFormat', 'TS201671', ['password'], accessFunctionForm))
      .map(removeUnnecessaryFields(entry, 'COGNYTE', 'deliveryFormat', 'TS33108', ['userPassword', 'ftptype', 'destPath'], accessFunctionForm))
      .map(unencodeFieldValues(entry, 'ERICSSON_LIMS', 'deliveryFormat', 'TS33108', ['netid'], accessFunctionForm))
      .map(convertBooleanValuesToTRUEorFALSE)
      .map(convertBooleanValuesToYN)
      .map(convertBooleanValuesToYesNo)
      .map(convertNodeNameValues(entry))
      .map(convertEncryptedBasedOnSecInfoValues(entry, accessFunctionForm?.type))
      .filter(removeSMFNodeTypeField)
      .filter(({ value }) => value != null);

    if (fileLocationFormData) {
      options.push(fileLocationFormData);
    }

    const accessFunctionInterface: AccessFunctionInterface = {
      name,
      id,
      deliveryVia,
      options,
    };

    // Remove any undefined entry
    return omitBy<AccessFunctionInterface>(accessFunctionInterface, isNil) as AccessFunctionInterface;
  });
}

function adaptUsnbdAgenciesToSubmit(
  accessFunctionInterfacesForm: AccessFunctionInterfaceFormData[] | undefined,
  accessFunctionForm?: AccessFunctionFormData,
  fileLocationFormData?: { key: string; value: string; },
): AccessFunctionInterface[] {
  if (!accessFunctionInterfacesForm) {
    return [];
  }

  return accessFunctionInterfacesForm.map((entry) => {
    let { id } = entry;

    const {
      id: _id,
      state: _state,
      deliveryVia,
      ...everythingElse
    } = entry;

    if (id?.includes(ACCESS_FUNCTION_INTERFACE_TEMPORARY_ID)) {
      id = '';
    }

    // Convert everything else to options and remove the null/undefined ones
    const options = Object.entries(everythingElse)
      .map(([key, value]) => ({ key, value }))
      .map(convertEncryptedValues)
      .map(convertBooleanValuesToTRUEorFALSE)
      .map(convertBooleanValuesToYN)
      .map(convertBooleanValuesToYesNo)
      .map(convertNodeNameValues(entry))
      .map(convertEncryptedBasedOnSecInfoValues(entry, accessFunctionForm?.type))
      .filter(removeSMFNodeTypeField)
      .filter(({ value }) => value != null);

    if (fileLocationFormData) {
      options.push(fileLocationFormData);
    }

    const accessFunctionInterface: AccessFunctionInterface = {
      id,
      deliveryVia,
      options,
    };

    // Remove any undefined entry
    return omitBy<AccessFunctionInterface>(accessFunctionInterface, isNil) as AccessFunctionInterface;
  });
}

function deliveryViaToForm(
  accessFunctionForm: AccessFunctionFormData,
): void {
  const {
    tag,
  } = accessFunctionForm;

  if (tag === 'ETSI103221') {
    if (accessFunctionForm.dataInterfaces && accessFunctionForm.dataInterfaces.length > 0) {
      accessFunctionForm.dataDeliveryVia = 'directFromAF';
    } else {
      accessFunctionForm.dataDeliveryVia = 'ss8Distributor';
    }

    if (accessFunctionForm.contentInterfaces && accessFunctionForm.contentInterfaces.length > 0) {
      accessFunctionForm.contentDeliveryVia = 'directFromAF';
    } else {
      accessFunctionForm.contentDeliveryVia = 'ss8Distributor';
    }
  }
}

function removeDeliveryViaFields(
  accessFunctionForm: AccessFunctionFormData,
): void {
  const {
    tag,
  } = accessFunctionForm;

  if (tag === 'ETSI103221') {
    delete accessFunctionForm.dataDeliveryVia;
    delete accessFunctionForm.contentDeliveryVia;
  }
}

function groupInterfacesToForm(
  accessFunctionForm: AccessFunctionFormData,
): void {
  const {
    type,
  } = accessFunctionForm;

  const configuration = GROUP_INTERFACES_FOR_AFS[type];
  if (configuration == null) {
    return;
  }

  const {
    fieldName,
    fieldValue,
    interfaces,
  } = configuration;

  if (accessFunctionForm[fieldName] === fieldValue) {
    interfaces.forEach((interfaceName) => {
      const interfaceData = accessFunctionForm[interfaceName];

      if (interfaceData == null || interfaceData.length === 0) {
        return;
      }

      const numberOfInterfaces = interfaceData.length;

      accessFunctionForm[interfaceName] = [{
        ...interfaceData[0],
        destPort: getPortForGroupInterfaces(interfaceData),
        maxx2connsperaf: numberOfInterfaces.toString(),
      }];
    });
  }
}

function getPortForGroupInterfaces(interfaceData: AccessFunctionInterfaceFormData[]): string {
  if (interfaceData.length > 4 || interfaceData[0].destPort === '0') {
    return '0';
  }

  return interfaceData.map(({ destPort }) => destPort).join(',');
}

function ungroupInterfacesToSubmit(
  accessFunctionForm: AccessFunctionFormData,
): void {

  const {
    type,
  } = accessFunctionForm;

  const configuration = GROUP_INTERFACES_FOR_AFS[type];
  if (configuration == null) {
    return;
  }

  const {
    fieldName,
    fieldValue,
    interfaces,
  } = configuration;

  if (accessFunctionForm[fieldName] === fieldValue) {
    interfaces.forEach((interfaceName) => {
      const interfaceData = accessFunctionForm[interfaceName];

      if (interfaceData == null || interfaceData.length === 0) {
        return;
      }

      const {
        id,
        name,
        maxx2connsperaf: numberOfInterfaces = '0',
        ...remainingFields
      } = interfaceData[0];

      const newInterfaces: AccessFunctionInterfaceFormData[] = [];
      for (let i = 0; i < +numberOfInterfaces; i += 1) {
        newInterfaces.push({
          ...remainingFields,
          id: '',
          name: '',
          destPort: getPortForInterface(remainingFields, i),
        });
      }

      accessFunctionForm[interfaceName] = newInterfaces;
    });
  }
}

function getPortForInterface(interfaceData: Partial<AccessFunctionInterfaceFormData>, index: number): string {
  const { destPort } = interfaceData;

  if (destPort == null || destPort === '0') {
    return '0';
  }

  return destPort.split(',').map((entry) => entry.trim())[index];
}

function convertYNValuesToBoolean({ key, value }: KeyValueUnknown): KeyValueUnknown {
  if (!YES_NO_FIELDS.includes(key)) {
    return { key, value };
  }

  return { key, value: value === 'Y' };
}

function convertYesNoValuesToBoolean({ key, value }: KeyValueUnknown): KeyValueUnknown {
  if (!YES_OR_NO_FIELDS.includes(key)) {
    return { key, value };
  }

  return { key, value: value === 'YES' };
}

function convertDefaultAnyAddressToEmptyString({ key, value }: KeyValueUnknown): KeyValueUnknown {

  if (key.toLowerCase() === 'ownIPAddr'.toLowerCase() && ANY_ADDRESSES.includes(value as string)) {
    return { key, value: '0.0.0.0' };
  }

  return { key, value };
}

function convertBooleanValuesToYN({ key, value }: KeyValueUnknown): KeyValueString {
  if (!YES_NO_FIELDS.includes(key)) {

    return { key, value: value as string };
  }

  if (value === true) {
    return { key, value: 'Y' };
  }

  return { key, value: 'N' };
}

function convertBooleanValuesToTRUEorFALSE({ key, value }: KeyValueUnknown): KeyValueString {
  if (!TRUE_FALSE_FIELDS.includes(key)) {
    return { key, value: value as string };
  }

  const convertedValue = value === true ? 'TRUE' : 'FALSE';
  return { key, value: convertedValue };
}

function convertBooleanValuesToYesNo({ key, value }: KeyValueUnknown): KeyValueString {
  if (!YES_OR_NO_FIELDS.includes(key)) {

    return { key, value: value as string };
  }

  if (value === true) {
    return { key, value: 'YES' };
  }

  return { key, value: 'NO' };
}

function convertToEnableDisabled({ key, value }: KeyValueUnknown): KeyValueString {
  if (!ENABLED_DISABLED_FIELDS.includes(key)) {
    return { key, value: value as string };
  }

  if (value === true) {
    return { key, value: 'ENABLED' };
  }

  return { key, value: 'DISABLED' };
}

function convertEncryptedValues({ key, value }: KeyValueUnknown): KeyValueUnknown | KeyValueNull {
  if (ENCRYPTED_FIELDS.includes(key) && value === ENCRYPTED_PLACEHOLDER) {
    return { key, value: null };
  }

  return { key, value };
}

function removeUnnecessaryFields(
  dataTab: AccessFunctionInterfaceFormData,
  tag: string,
  field: keyof AccessFunctionInterfaceFormData,
  nullableValue: string,
  optionsToNull: string[],
  accessFunctionForm?: AccessFunctionFormData,
): ({ key, value }: KeyValueUnknown) => KeyValueUnknown | KeyValueNull {
  return ({ key, value }: KeyValueUnknown): KeyValueUnknown | KeyValueNull => {

    if (accessFunctionForm == null || accessFunctionForm.tag !== tag || !Object.keys(dataTab).includes(field)) {
      return { key, value };
    }

    if (dataTab[field] === nullableValue && optionsToNull.includes(key)) {
      return { key, value: null };
    }

    return { key, value };
  };
}

function unencodeFieldValues(
  dataTab: AccessFunctionInterfaceFormData,
  tag: string,
  field: keyof AccessFunctionInterfaceFormData,
  nullableValue: string,
  optionsToEncode: string[],
  accessFunctionForm?: AccessFunctionFormData
): ({ key, value }: KeyValueUnknown) => KeyValueUnknown | KeyValueNull {
  return ({ key, value }: KeyValueUnknown): KeyValueUnknown | KeyValueNull => {
    if (
      accessFunctionForm == null ||
      accessFunctionForm.tag !== tag ||
      !Object.keys(dataTab).includes(field)
    ) {
      return { key, value };
    }

    if (dataTab[field] === nullableValue && optionsToEncode.includes(key)) {
      return {
        key,
        value: Buffer.from((dataTab[key as keyof AccessFunctionInterfaceFormData] ?? '') as string, 'base64').toString('binary')
      };
    }

    return { key, value };
  };
}

function adaptDiameterMoFields(
  accessFunctionForm: AccessFunction,
): void {
  if (!accessFunctionForm || !accessFunctionForm?.diameter || accessFunctionForm?.tag !== 'ERICSSON_IMS') {
    return;
  }

  const diameterId = accessFunctionForm.diameter[0].options?.find((option: KeyValueUnknown) => option.key === 'diameterId')?.value ?? '';

  const additionalMOs = accessFunctionForm.diameter
    ?.map((entry) => ({
      moName: 'DIAMETER',
      id: diameterId as string,
      options: entry.options
        .filter(removeDiameterIdOption)
        .map(convertToEnableDisabled),
    }));

  accessFunctionForm.additionalMOs = additionalMOs;
}

function removeDiameterIdOption({ key }: KeyValueUnknown): boolean {
  return key !== 'diameterId';
}

export function adaptAccessFunction(accessFunction: AccessFunction): AccessFunction {
  if (accessFunction == null) {
    return accessFunction;
  }

  if (SERVER_AF_TYPES.includes(accessFunction.type)) {
    accessFunction.tag = accessFunction.tag.substring(0, accessFunction.tag.length - 4);
  }

  return {
    ...accessFunction,
    tag: accessFunction != null && accessFunction.tag === 'VRNT_SG' && accessFunction.version === '7.1' ? 'COGNYTE' : accessFunction.tag,
  };
}

const removeSMFNodeTypeField = ({ key }: { key: string, value: string }) => {
  if (key === 'nodeNameType' || key === 'mdf3NodeName') {
    return false;
  }

  return true;
};

const convertNodeNameValues = (accessFunctionForm: AccessFunctionInterfaceFormData) =>
  ({ key, value }: { key: string, value: string }) => {

    if (key === 'ccnodename' && !accessFunctionForm.ccnodename && accessFunctionForm.mdf3NodeName) {
      return {
        key,
        value: accessFunctionForm.mdf3NodeName,
      };
    }

    return {
      key,
      value,
    };
  };

const convertEncryptedBasedOnSecInfoValues = (accessFunctionForm: AccessFunctionInterfaceFormData, afType?: string) =>
  ({ key, value }: { key: string, value: string }) => {

    if (key !== 'encryption' || afType !== 'ACME_X123') {
      return {
        key,
        value,
      };
    }

    const encryptionValue = accessFunctionForm?.secinfoid === 'Tk9ORQ' ? 'DISABLED' : 'ENABLED';

    return {
      key,
      value: encryptionValue,
    };
  };

export function getEnabledPoiAccessFunctions(configuredAccessFunction: ConfiguredAccessFunction[], enabledAccessFunction: SupportedFeaturesAccessFunctions) {
  const enabledPoiAccessFunctions: SupportedFeaturesAccessFunctions = {};
  const configuredAFTags: string[] = configuredAccessFunction?.map((caf) => caf.afTag);
  Object.entries(enabledAccessFunction).forEach(([accessFunctionName, accessFunctionConfig]) => {
    if (configuredAFTags?.includes(accessFunctionName)) {
      enabledPoiAccessFunctions[accessFunctionName] = accessFunctionConfig;
    }
  });
  return enabledPoiAccessFunctions;
}

export function getEnabledAccessFunctions(availableAccessFunctions: SupportedFeaturesAccessFunctions, enabledFeatures: string[]) {
  const enabledAccessFunctions: SupportedFeaturesAccessFunctions = {};
  Object.entries(availableAccessFunctions).forEach(([accessFunctionTag, accessFunctionConfig]) => {
    if (accessFunctionConfig.features.some((feature) => enabledFeatures.includes(feature))) {
      enabledAccessFunctions[accessFunctionTag] = accessFunctionConfig;
    }
  });
  return enabledAccessFunctions;
}

export function getFilteredAccessFunctions(accessFunctions: AccessFunction[], enabledPoiAccessFunctions: SupportedFeaturesAccessFunctions): AccessFunction[] {
  return accessFunctions.filter(({ tag }) => !!enabledPoiAccessFunctions[tag]);
}
